/**
 * Making LLM-authored text printable by jsPDF's standard fonts.
 *
 * Promoted here from `changeAnalysisReport` (MYAC-1162), where it had already
 * outgrown its home: the gap-analysis generator imported `toPrintable` across a
 * module boundary and kept its own copy of `sanitizeDeep`, and the authored-document
 * export would have been the third. Two copies of a substitution table are two things
 * to keep in step.
 *
 * The problem it solves is not cosmetic. A SINGLE character outside WinAnsi — one `→`
 * in a rationale, one `✓` in a table cell — makes jsPDF fall back to a 2-byte encoding
 * for the WHOLE string, which Helvetica then renders as letter-spaced garbage.
 * Embedding a Unicode font is the real fix and a far bigger change (every generator,
 * hundreds of KB); transliterating is what keeps the page readable meanwhile.
 */

/**
 * WinAnsi's own slots above ASCII (0x80–0x9F) — jsPDF's standard fonts carry
 * these, so they survive as themselves.
 */
const WINANSI_SPECIALS = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ'

/** ASCII stand-ins for the glyphs an LLM most often reaches for outside WinAnsi. */
const SUBSTITUTIONS: Record<string, string> = {
  '→': '->',
  '⇒': '=>',
  '←': '<-',
  '⇐': '<=',
  '↔': '<->',
  '≥': '>=',
  '≤': '<=',
  '≠': '!=',
  '≈': '~',
  '−': '-',
  '‑': '-',
  '‣': '-',
  '▪': '-',
  '◦': '-',
  '✓': '[ok]',
  '✔': '[ok]',
  '✗': '[x]',
  '✘': '[x]',
  '№': 'No.',
}

/**
 * Make a string safe for jsPDF's standard (WinAnsi-encoded) fonts.
 *
 * A single character outside that encoding — the `→` in the dismissed-impacts
 * caption, an arrow or a check mark in a worker's rationale — makes jsPDF fall
 * back to a 2-byte encoding for the WHOLE string, which Helvetica then renders
 * as letter-spaced garbage. Embedding a Unicode font would be the real fix and
 * is a far bigger change (every generator, +hundreds of KB of font); for a
 * record whose content is LLM-authored free text, transliterating is what keeps
 * the page readable.
 *
 * Exported for its own test: this runs over every string in the document, so it
 * must not mangle the accented French/English text that is the normal case.
 */
export function toPrintable(text: string): string {
  // Printable ASCII + Latin-1, plus the newlines/tabs the markdown renderer
  // relies on; everything else goes through the table below.
  return text.replace(/[^\x09\x0a\x20-\x7e\xa0-\xff]/g, (char) => {
    if (WINANSI_SPECIALS.includes(char)) return char
    // Never silently drop: a record should say that something was here.
    return SUBSTITUTIONS[char] ?? '?'
  })
}

/** Same, over every string of a (plain, JSON-shaped) view payload. */
export function sanitizeDeep<T>(value: T): T {
  if (typeof value === 'string') return toPrintable(value) as T
  if (Array.isArray(value)) return value.map((item) => sanitizeDeep(item)) as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) out[key] = sanitizeDeep(item)
    return out as T
  }
  return value
}
